import { Controller } from "@hotwired/stimulus"

const WIDE = "(min-width: 1024px)"

// To do: the focused row (J/K), selection for bulk actions (X), and the decision shortcuts
// (A approve, R reject, E edit, S snooze, ⌘Z undo). On wide screens a row opens in the side pane;
// on narrow ones it opens as its own page.
export default class extends Controller {
  static targets = ["row", "checkbox", "bulkBar", "bulkForm", "selectedCount"]

  connect() {
    this.focusedKey = this.element.dataset.todoFocus || null
    this.syncBulk()
  }

  get wide() {
    return window.matchMedia(WIDE).matches
  }

  open(event) {
    const row = event.currentTarget.closest("[data-todo-target~='row']")
    this.focus(row, { scroll: false })
    if (!this.wide) {
      event.preventDefault()
      window.Turbo.visit(event.currentTarget.href)
    }
  }

  focus(row, { load = false, scroll = true } = {}) {
    if (!row) return
    this.rowTargets.forEach((each) => each.toggleAttribute("data-focused", each === row))
    this.focusedKey = row.dataset.key
    if (scroll) row.scrollIntoView({ block: "nearest" })
    if (load && this.wide) row.querySelector("a[data-turbo-frame]")?.click()
  }

  liveRows() {
    return this.rowTargets.filter((row) => !row.hasAttribute("data-done"))
  }

  focusedRow() {
    return this.rowTargets.find((row) => row.dataset.key === this.focusedKey)
  }

  move(step) {
    const rows = this.liveRows()
    if (rows.length === 0) return
    const index = rows.findIndex((row) => row.dataset.key === this.focusedKey)
    const next = index === -1 ? rows[0] : rows[Math.min(Math.max(index + step, 0), rows.length - 1)]
    this.focus(next, { load: true })
  }

  // The row to show once the focused one is dealt with: the next one down, else the one above
  nextKeyAfter(key) {
    const rows = this.liveRows()
    const index = rows.findIndex((row) => row.dataset.key === key)
    if (index === -1) return null
    return (rows[index + 1] || rows[index - 1])?.dataset.key || null
  }

  selected() {
    return this.checkboxTargets.filter((box) => box.checked)
  }

  toggle() {
    this.syncBulk()
  }

  clearSelection() {
    this.checkboxTargets.forEach((box) => (box.checked = false))
    this.syncBulk()
  }

  syncBulk() {
    if (!this.hasBulkBarTarget) return
    const count = this.selected().length
    this.bulkBarTarget.classList.toggle("hidden", count === 0)
    this.bulkBarTarget.classList.toggle("flex", count > 0)
    this.selectedCountTargets.forEach((element) => (element.textContent = count))
  }

  // Tells the server which item the side pane shows and which comes next, so it can move on
  fillContext(event) {
    const form = event.target
    const set = (name, value) => {
      let input = form.querySelector(`input[name='${name}']`)
      if (!input) {
        input = Object.assign(document.createElement("input"), { type: "hidden", name })
        form.appendChild(input)
      }
      input.value = value || ""
    }
    if (this.focusedKey && this.rowTargets.length) {
      set("focus", this.focusedKey)
      if (!form.querySelector("input[name='next']")?.value) set("next", this.nextKeyAfter(this.focusedKey))
    }
    this.pendingNext = this.nextKeyAfter(this.focusedKey)
  }

  submitEnd(event) {
    if (!event.detail.success) return
    requestAnimationFrame(() => {
      const row = this.focusedRow()
      if (row?.hasAttribute("data-done") && this.pendingNext) {
        const next = this.rowTargets.find((each) => each.dataset.key === this.pendingNext)
        this.focus(next, { scroll: true })
      }
      this.syncBulk()
    })
  }

  hotkey(event) {
    if (event.defaultPrevented || this.typing(event.target)) return
    if (document.documentElement.hasAttribute("data-drawer-open")) return

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
      const undo = [...document.querySelectorAll("form[data-todo-undo]")].pop()
      if (undo) {
        event.preventDefault()
        undo.requestSubmit()
      }
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey || document.querySelector("dialog[open]")) return

    const key = event.key.toLowerCase()
    switch (key) {
      case "j":
        return this.prevent(event, () => this.move(1))
      case "k":
        return this.prevent(event, () => this.move(-1))
      case "x": {
        const box = this.focusedRow()?.querySelector("[data-todo-target~='checkbox']")
        if (box) this.prevent(event, () => { box.checked = !box.checked; this.syncBulk() })
        return
      }
      case "escape":
        return this.clearSelection()
      case "a":
      case "r":
      case "s":
        if (this.selected().length && this.hasBulkFormTarget) {
          const button = this.bulkFormTarget.querySelector(`[data-todo-bulk='${{ a: "approve", r: "reject", s: "snooze" }[key]}']`)
          return this.prevent(event, () => this.bulkFormTarget.requestSubmit(button))
        }
        return this.clickHotkey(event, key)
      case "e":
        return this.clickHotkey(event, key)
    }
  }

  // The focused item's button for a shortcut: in the side pane (or item page), else in its row
  clickHotkey(event, key) {
    const pane = document.querySelector(`[data-todo-item='${this.focusedKey}']`) || (this.rowTargets.length ? null : document.querySelector("[data-todo-item]"))
    const target = pane?.querySelector(`[data-todo-hotkey='${key}']`) || this.focusedRow()?.querySelector(`[data-todo-hotkey='${key}']`)
    if (!target) return
    this.prevent(event, () => {
      if (target.tagName === "A") return target.click()
      const form = target.closest("form")
      form ? form.requestSubmit(target.tagName === "BUTTON" ? target : undefined) : target.click()
    })
  }

  prevent(event, action) {
    event.preventDefault()
    action()
  }

  typing(element) {
    if (element.tagName === "INPUT") return !["checkbox", "radio", "button", "submit"].includes(element.type)
    return element.isContentEditable || ["TEXTAREA", "SELECT"].includes(element.tagName)
  }
}
